import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { setSession, useLogout } from "../../auth/session.js";
import { AuthLayout, Button, ErrorNotice, Field, Input } from "../../components/form.js";

export function MfaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const logout = useLogout();
  const [mode, setMode] = useState<"code" | "recovery">("code");
  const [value, setValue] = useState("");

  const verify = useMutation({
    mutationFn: () =>
      api.auth.mfa.verify(mode === "code" ? { code: value } : { recoveryCode: value }),
    onSuccess: (session) => {
      setSession(queryClient, session);
      void navigate({ to: "/mail" });
    },
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    verify.mutate();
  };

  return (
    <AuthLayout title={t("auth.mfa.title")} subtitle={t("auth.mfa.subtitle")}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field label={mode === "code" ? t("auth.mfa.code") : t("auth.mfa.recoveryCode")}>
          <Input
            required
            autoFocus
            autoComplete="one-time-code"
            inputMode={mode === "code" ? "numeric" : "text"}
            pattern={mode === "code" ? "[0-9]{6}" : undefined}
            placeholder={mode === "code" ? "123456" : "xxxxx-xxxxx"}
            className="font-mono tracking-widest"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </Field>
        <ErrorNotice error={verify.error} />
        <Button type="submit" disabled={verify.isPending}>
          {verify.isPending ? t("common.loading") : t("auth.mfa.submit")}
        </Button>
        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            className="text-accent underline-offset-2 hover:underline"
            onClick={() => {
              setMode(mode === "code" ? "recovery" : "code");
              setValue("");
            }}
          >
            {mode === "code" ? t("auth.mfa.useRecovery") : t("auth.mfa.useCode")}
          </button>
          <button
            type="button"
            className="text-ink-3 underline-offset-2 hover:underline"
            onClick={() =>
              logout.mutate(undefined, { onSettled: () => void navigate({ to: "/login" }) })
            }
          >
            {t("auth.signOut")}
          </button>
        </div>
      </form>
    </AuthLayout>
  );
}
